import type { AssocBasicInfo } from "../../admin/utils/basicInfoStore";

/** 行为准则条目（与具体城市/队名无关） */
export const ENROLLMENT_CONDUCT_RULES: { icon: string; title: string; desc: string }[] = [
  { icon: "ri-heart-line", title: "尊重他人", desc: "尊重其他球迷、球员及工作人员，禁止人身攻击和歧视性言论" },
  { icon: "ri-shield-check-line", title: "文明观赛", desc: "遵守赛场与活动秩序，不扰乱公共秩序，不做出危险行为" },
  { icon: "ri-flag-line", title: "维护协会形象", desc: "在公共场合以积极正面的方式代表协会，爱护集体声誉" },
  { icon: "ri-information-line", title: "真实信息", desc: "不传播未经证实的谣言，以官方渠道信息为准" },
  { icon: "ri-lock-line", title: "保护隐私", desc: "未经许可不得发布其他会员的个人信息和照片" },
  { icon: "ri-team-line", title: "积极参与", desc: "积极参加活动，互帮互助，共同维护良好的球迷社区氛围" },
];

export function enrollmentAssocLabel(info: AssocBasicInfo): string {
  return (info.shortName?.trim() || info.name?.trim() || "球迷协会").trim() || "球迷协会";
}

/** 是否在租户资料中配置了对公账户（任一项非空即认为已配置） */
export function hasTenantBankInfo(info: AssocBasicInfo): boolean {
  return Boolean(
    info.enrollmentPayee?.trim() ||
      info.enrollmentBankName?.trim() ||
      info.enrollmentBankAccount?.trim(),
  );
}

/**
 * 通用入会协议正文；组织名来自租户资料（tenant-profile）。
 * 完整法定文本以管理后台发布的章程/协议文件为准。
 */
export function buildEnrollmentAgreementText(orgLabel: string): string {
  const o = orgLabel.trim() || "本协会";
  return `${o}入会协议

本协议中「本协会」指「${o}」。下列条款为入会基本约定；如与协会章程或已公示的正式协议不一致，以章程及正式文件为准。

一、会员权利
1. 会员享有参与本协会举办的各类活动的权利；
2. 会员享有对本协会工作提出意见和建议的权利；
3. 会员享有依规定获得本协会提供的服务和帮助的权利；
4. 会员享有按规定退会的自由。

二、会员义务
1. 会员应遵守协会章程及决议，遵守国家法律法规；
2. 会员应维护协会合法权益与声誉；
3. 会员应积极参与协会活动，履行会员职责；
4. 会员应如实提供个人资料，配合协会核实；
5. 会员应按协会规定缴纳会费（如有）。

三、会员等级
会员等级及积分规则以协会公示或管理后台配置为准；等级可随积分变化调整。

四、隐私保护
协会依法收集、使用和保护会员个人信息，除法律要求或会员同意外不向第三方泄露。

五、退出与除名
1. 会员可按规定提出退会申请；
2. 严重违反章程、本协议或行为准则的，经理事会或授权机构决议可予以除名。

本协议自会员签署之日起生效。`;

}

/**
 * 会员端入会缴费说明：已配置对公账户则展示账户行，否则引导通过官方渠道索取。
 */
export function buildEnrollmentPaymentRows(
  info: AssocBasicInfo,
  orgLabel: string,
): { label: string; value: string }[] {
  const o = orgLabel.trim() || "协会";
  const payee = info.name?.trim() || info.shortName?.trim() || o;

  if (hasTenantBankInfo(info)) {
    const rows: { label: string; value: string }[] = [
      {
        label: "缴费说明",
        value: "请按协会公示或审核通知的金额缴纳会费；转账时请核对下方收款信息，勿向非官方渠道转账。",
      },
    ];
    if (info.enrollmentPayee?.trim()) rows.push({ label: "收款单位", value: info.enrollmentPayee.trim() });
    if (info.enrollmentBankName?.trim()) rows.push({ label: "开户银行", value: info.enrollmentBankName.trim() });
    if (info.enrollmentBankAccount?.trim()) rows.push({ label: "银行账号", value: info.enrollmentBankAccount.trim() });
    rows.push({
      label: "转账备注",
      value: info.enrollmentTransferNote?.trim() || "姓名 + 入会缴费",
    });
    rows.push(
      { label: "联系电话", value: info.phone?.trim() || "—" },
      { label: "微信/公众号", value: info.wechat?.trim() || "—" },
      { label: "电子邮箱", value: info.email?.trim() || "—" },
    );
    return rows;
  }

  return [
    {
      label: "缴费说明",
      value: `请按协会公示或审核通知的金额缴纳会费；收款账户、开户行等信息请通过微信、电话或邮件向「${o}」确认，勿向非官方渠道转账。`,
    },
    { label: "收款抬头（参考）", value: payee },
    { label: "联系电话", value: info.phone?.trim() || "—" },
    { label: "微信/公众号", value: info.wechat?.trim() || "—" },
    { label: "电子邮箱", value: info.email?.trim() || "—" },
    { label: "转账备注", value: "姓名 + 入会缴费" },
  ];
}

/** 管理端审核：对应缴金额旁展示系统配置的收款账户或索取指引 */
export function buildAuditFeePaymentRows(
  info: AssocBasicInfo,
  orgLabel: string,
  amountLine: string,
): { label: string; value: string }[] {
  const o = orgLabel.trim() || "协会";
  if (hasTenantBankInfo(info)) {
    const rows: { label: string; value: string }[] = [];
    if (info.enrollmentPayee?.trim()) rows.push({ label: "收款单位", value: info.enrollmentPayee.trim() });
    if (info.enrollmentBankName?.trim()) rows.push({ label: "开户银行", value: info.enrollmentBankName.trim() });
    if (info.enrollmentBankAccount?.trim()) rows.push({ label: "银行账号", value: info.enrollmentBankAccount.trim() });
    if (info.enrollmentTransferNote?.trim()) rows.push({ label: "转账备注", value: info.enrollmentTransferNote.trim() });
    rows.push({ label: "应缴金额", value: amountLine });
    return rows;
  }
  return [
    {
      label: "缴费说明",
      value: `收款账户请向「${o}」官方渠道确认后再核对凭证；勿仅凭历史示例账号打款。`,
    },
    { label: "联系电话", value: info.phone?.trim() || "—" },
    { label: "微信/公众号", value: info.wechat?.trim() || "—" },
    { label: "应缴金额", value: amountLine },
  ];
}

/** 会员续费弹窗中的缴费信息行 */
export function buildRenewalPaymentRows(
  info: AssocBasicInfo,
  orgLabel: string,
  annualFee: number,
  feeLabel: string,
  memberName: string,
): { label: string; value: string }[] {
  const o = orgLabel.trim() || "协会";
  if (hasTenantBankInfo(info)) {
    const rows: { label: string; value: string }[] = [];
    if (info.enrollmentPayee?.trim()) rows.push({ label: "收款单位", value: info.enrollmentPayee.trim() });
    if (info.enrollmentBankName?.trim()) rows.push({ label: "开户银行", value: info.enrollmentBankName.trim() });
    if (info.enrollmentBankAccount?.trim()) rows.push({ label: "银行账号", value: info.enrollmentBankAccount.trim() });
    rows.push({ label: "转账金额", value: `¥${annualFee.toFixed(0)}（${feeLabel}）` });
    rows.push({
      label: "转账备注",
      value: info.enrollmentTransferNote?.trim() || `${memberName} 会员续费`,
    });
    return rows;
  }
  return [
    {
      label: "缴费说明",
      value: `续费收款信息请通过微信、电话或邮件向「${o}」确认后再转账。`,
    },
    { label: "联系电话", value: info.phone?.trim() || "—" },
    { label: "微信/公众号", value: info.wechat?.trim() || "—" },
    { label: "转账金额", value: `¥${annualFee.toFixed(0)}（${feeLabel}）` },
    { label: "转账备注", value: `${memberName} 会员续费` },
  ];
}

export function buildConductPledgeLabel(orgLabel: string): string {
  const o = orgLabel.trim() || "协会";
  return `我已阅读并承诺遵守《${o}行为准则》，理解违反准则可能导致会员资格被取消。`;
}
